import { useEffect, useState, useCallback, useRef } from 'react';
import { Close } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import saveAs from 'file-saver';
import {
  Card,
  Divider,
  NativeSelect,
  Skeleton,
  Stack,
  Typography,
  Button,
  DialogTitle,
  DialogContent,
  useTheme,
  IconButton,
} from '@mui/material';

import { getPatientInfo, getPatientBarcode } from 'apis/admin';
import useI18n from 'hooks/useI18n';
import useNotification from 'hooks/useNotification';
import useStudent from 'store/student/useStudent';
import usePatient from 'store/patient/usePatient';
import PatientInfoItem from './PatientInfoItem';
import Modal from './Modal';
import { getLocalStorage, setLocalStorage } from 'utils/storage';
import ConfirmButton from './Components/ConfirmButton';
import useUser from 'store/user/useUser';

// 메인화면 중앙 상단 환자 및 담당 의료진 정보
const PatientInfo = () => {
  const [isOpenModal, setOpenModal] = useState<boolean>(false);
  const [isTestMode, setIsTestMode] = useState(
    sessionStorage.getItem('MODE') === 'TEST'
  );
  const { onFail } = useNotification();
  const { student_name: userName } = useStudent();
  const { patient, patientInfo, onSelectedPatientInfo } = usePatient();
  const { isStudent } = useUser();
  // 시험 모드
  const handleTestMode = useCallback(() => {
    setIsTestMode(prevMode => {
      if (prevMode) return prevMode;

      setLocalStorage('theme_color', 'RED');
      document.body.style.userSelect = 'none';
      document.addEventListener('contextmenu', e => e.preventDefault());
      sessionStorage.setItem('MODE', 'TEST');

      return true;
    });
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }, []);

  useEffect(() => {
    if (isTestMode) {
      document.body.style.userSelect = 'none';
      document.addEventListener('contextmenu', e => e.preventDefault());
    }

    return () => {
      document.removeEventListener('contextmenu', e => e.preventDefault());
    };
  }, [isTestMode]);

  // 바코드 모달창 만들기
  const { zIndex, breakpoints } = useTheme();
  const onClickToggleModal = useCallback(() => {
    setOpenModal(!isOpenModal);
  }, [isOpenModal]);

  const divRef = useRef<HTMLDivElement>(null);

  const handleDownload = async () => {
    if (!divRef.current) return;

    try {
      const div = divRef.current;
      const canvas = await html2canvas(div, { scale: 2 });
      canvas.toBlob(blob => {
        if (blob !== null) {
          saveAs(blob, `${userName}-${patientInfo?.name}.png`);
        }
      });
    } catch (error) {
      console.error('Error converting div to image:', error);
    }
  };

  // 부진단 코드 index
  const [diseaseSubIndex, setDiseaseSubIndex] = useState(0);

  useEffect(() => {
    if (!patient) return;

    // 가상환자 상세정보 요청
    getPatientInfo({ patient_id: patient.patient_id })
      .then(({ data }) => {
        onSelectedPatientInfo(data);
      })
      .catch(e => {
        onSelectedPatientInfo(null);
        onFail(`가상환자 데이터 조회에 실패했습니다.`, e);
      });
    // eslint-disable-next-line
  }, [patient]);

  // 바코드 변수에 담기
  const [imgBarcode, setImgBarcode] = useState('');

  useEffect(() => {
    if (!patient) return;

    // 가상환자 상세정보 요청
    getPatientBarcode({ patient_id: patient.patient_id })
      .then(({ data }) => {
        setImgBarcode(data);
      })
      .catch(e => {
        onSelectedPatientInfo(null);
        onFail(`가상환자 데이터 조회에 실패했습니다.`, e);
      });
    // eslint-disable-next-line
  }, [patient]);

  if (!patientInfo) {
    return <Skeleton variant="rectangular" sx={{ flex: 1 }} />;
  }

  const {
    // column1
    patient_id,
    name,
    age,
    gender,
    blood,
    // column2
    height,
    weight,
    admin_hod,
    admin_pod,
    // column3
    department,
    ward,
    room,
    main_doctor,
    // column4
    disease_main,
    disease_sub,
    fc,
  } = patientInfo;

  // 부진단 코드 선택 UI
  const DiseaseSubCode = () => (
    <NativeSelect
      value={diseaseSubIndex}
      onChange={e => setDiseaseSubIndex(Number(e.target.value))}
      inputProps={{ style: { paddingTop: 0, paddingBottom: 0 } }}
      sx={{ fontSize: 14, lineHeight: '18px' }}
    >
      {disease_sub?.map(({ disease_id }, index) => (
        <option key={disease_id} value={index}>
          {disease_id}
        </option>
      ))}
    </NativeSelect>
  );

  const convertHeight = (heightStr: string) => {
    // "160cm"에서 숫자만 추출
    const cmValue = parseFloat(heightStr);
    if (isNaN(cmValue)) return heightStr;
    // cm를 인치로 변환
    const totalInches = cmValue / 2.54;
    // 전체 인치에서 피트와 남은 인치 계산
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches - feet * 12);
    return `${cmValue}cm (${feet}'${inches}")`;
  };

  const convertWeight = (weightStr: string) => {
    // "52kg"에서 숫자만 추출
    const kgValue = parseFloat(weightStr);
    if (isNaN(kgValue)) return weightStr;
    // kg를 파운드로 변환
    const pounds = kgValue * 2.20462;
    return `${kgValue}kg (${pounds.toFixed(1)} lbs)`;
  };

  return (
    <div style={{ zIndex: 1000 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="subtitle2" fontSize={13} mb={1}>
          {name} Patient Info
        </Typography>
        <Typography sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {isStudent ? (
            <>
              <span
                style={{
                  fontSize: '12px',
                  color: 'gray',
                  marginBottom: '10px',
                }}
              >
                {isTestMode
                  ? 'If you want to exit Exam Mode, you need to log out and log back in.'
                  : ''}
              </span>
              <ConfirmButton
                title="Would you like to start Exam Mode?"
                isTestMode={isTestMode}
                handleTestMode={handleTestMode}
              />
            </>
          ) : null}

          <Button
            variant="contained"
            size="small"
            sx={{ marginBottom: '5px', marginTop: '-10px' }}
            onClick={onClickToggleModal}
          >
            Patient Label Printing
          </Button>
        </Typography>
        {isOpenModal && (
          <Modal onClickToggleModal={onClickToggleModal}>
            <DialogTitle
              display="flex"
              alignItems="center"
              position="sticky"
              sx={{
                top: 0,
                zIndex: zIndex.modal,
                background: '#fff',
                borderBottom: '1px solid rgba(0, 0, 0, 0.3)',
              }}
            >
              <IconButton size="small" sx={{ mr: 1.5 }}>
                <Close color="primary" onClick={onClickToggleModal} />
              </IconButton>
              Patient Label printing
            </DialogTitle>
            <DialogContent>
              <div
                onClick={handleDownload}
                ref={divRef}
                style={{
                  width: '288px',
                  height: '90px',
                  backgroundColor: 'white',
                  cursor: 'pointer',

                  marginTop: '50px',
                  padding: '3px 10px 3px 10px',
                  alignItems: 'center',
                  justifyContent: 'center',

                  display: 'flex',
                }}
              >
                <div
                  style={{
                    width: '170px',
                  }}
                >
                  <div>
                    <Typography sx={{ fontSize: '10px' }}>
                      {patient_id}
                    </Typography>
                  </div>

                  <div>
                    <Typography sx={{ fontSize: '10px' }}>
                      {ward}/{room}
                    </Typography>
                  </div>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600 }}>
                    {name}
                  </Typography>
                  <div style={{ display: 'flex', gap: '20px' }}>
                    <Typography sx={{ fontSize: '10px' }}>
                      {age}/{gender === 2 ? '남자' : '여자'}
                    </Typography>
                    <Typography sx={{ fontSize: '10px' }}>{blood}</Typography>
                  </div>
                </div>

                <div style={{}}>
                  <img
                    src={imgBarcode}
                    alt="바코드"
                    style={{ height: '30px', marginLeft: '10px' }}
                  />
                </div>
              </div>
            </DialogContent>
          </Modal>
        )}
      </div>

      <Card
        component="section"
        sx={{
          p: 2.5,
          display: 'flex',
          overflow: 'visible',
          justifyContent: 'space-around',
          gap: { xs: 1, xl: 3 },
        }}
      >
        <Stack spacing={1.4} sx={{ flex: '0 0 auto', minWidth: '140px' }}>
          <PatientInfoItem title="Reg. No." content={patient_id} />
          <PatientInfoItem title="Name" content={name} />
          <PatientInfoItem title="Age" content={age} />
          <PatientInfoItem title="Blood Type" content={blood} />
        </Stack>
        <Divider orientation="horizontal" />
        <Stack spacing={1.4} sx={{ flex: '0 0 auto', minWidth: '160px' }}>
          <PatientInfoItem title="Height" content={convertHeight(height)} />
          <PatientInfoItem title="Weight" content={convertWeight(weight)} />
          <PatientInfoItem title="HOD" content={admin_hod} />
          <PatientInfoItem title="POD" content={admin_pod} />
        </Stack>

        <Divider orientation="horizontal" />
        <Stack spacing={1.4} sx={{ flex: '1 1 auto', minWidth: '180px' }}>
          <PatientInfoItem title="Dept." content={department} />
          <PatientInfoItem title="Ward" content={ward} />
          <PatientInfoItem title="Room" content={room} />
          <PatientInfoItem title="Lead RN." content={userName} />
          <PatientInfoItem title="Lead MD." content={main_doctor} />
        </Stack>
        <Divider orientation="horizontal" />
        <Stack spacing={1.4} sx={{ flex: '0 0 auto', minWidth: '200px' }}>
          <PatientInfoItem title="Code" content={disease_main.disease_id} />
          <PatientInfoItem
            title="Diagnosis"
            content={disease_main.disease_eng}
          />
          <PatientInfoItem
            title="Code"
            content={disease_sub.length > 0 ? <DiseaseSubCode /> : null}
          />
          <PatientInfoItem
            title="Sub Dx."
            content={disease_sub[diseaseSubIndex]?.disease_eng}
          />
          <PatientInfoItem
            title="FC"
            content={
              <Typography color="error.main" sx={{ fontSize: 14 }}>
                {fc}
              </Typography>
            }
          />
        </Stack>
      </Card>
    </div>
  );
};

export default PatientInfo;
